// src/components/auth/Register.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const PASSWORD_RULES = [
    { id: 'length', label: 'Minimo 8 caratteri', test: p => p.length >= 8 },
    { id: 'upper', label: 'Una lettera maiuscola', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'Una lettera minuscola', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'Un numero', test: p => /[0-9]/.test(p) },
];

// Logo InfraPilot SVG
const InfraPilotLogo = ({ size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#1d4ed8" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#2563eb" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="white" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="white">P</text>
    </svg>
);

const Register = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', password: '',
        confirmPassword: '', organizationName: '', acceptTerms: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const isPasswordValid = (p) => PASSWORD_RULES.every(r => r.test(p));

    const validate = () => {
        const e = {};
        if (!formData.firstName.trim()) e.firstName = 'Nome richiesto';
        if (!formData.lastName.trim()) e.lastName = 'Cognome richiesto';
        if (!formData.email.trim()) e.email = 'Email richiesta';
        else if (!validateEmail(formData.email.trim())) e.email = 'Email non valida';
        if (!formData.organizationName.trim()) e.organizationName = 'Nome organizzazione richiesto';
        if (!formData.password) e.password = 'Password richiesta';
        else if (!isPasswordValid(formData.password)) e.password = 'La password non soddisfa i requisiti';
        if (!formData.confirmPassword) e.confirmPassword = 'Conferma la password';
        else if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Le password non coincidono';
        if (!formData.acceptTerms) e.acceptTerms = 'Devi accettare i termini';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        const result = await register({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
            organizationName: formData.organizationName.trim(),
        });
        setLoading(false);
        if (result.success) {
            navigate('/login', { state: { message: 'Account creato! Accedi con le tue credenziali.' } });
        } else {
            setErrors({ general: result.error || 'Errore durante la registrazione' });
        }
    };

    const score = PASSWORD_RULES.filter(r => r.test(formData.password)).length;
    const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-emerald-500'];
    const strengthLabels = ['Molto debole', 'Debole', 'Buona', 'Forte'];
    const ic = (err) => `w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 py-12 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-md w-full mx-4">

                {/* Brand */}
                <div className="text-center mb-6">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <InfraPilotLogo size={44} />
                        <div className="text-left">
                            <h1 className="text-2xl font-bold text-white">InfraPilot</h1>
                            <p className="text-blue-300 text-xs">Construction Site Management</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-0.5">Crea il tuo account</h2>
                    <p className="text-gray-500 text-sm mb-5">14 giorni di prova gratuita · Nessuna carta richiesta</p>

                    {errors.general && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                            ⚠️ {errors.general}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome *</label>
                                <input type="text" name="firstName" autoComplete="given-name" value={formData.firstName}
                                    onChange={handleChange} className={ic(errors.firstName)} placeholder="Mario" />
                                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cognome *</label>
                                <input type="text" name="lastName" autoComplete="family-name" value={formData.lastName}
                                    onChange={handleChange} className={ic(errors.lastName)} placeholder="Rossi" />
                                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email *</label>
                            <input type="email" name="email" autoComplete="email" value={formData.email}
                                onChange={handleChange} className={ic(errors.email)} placeholder="mario@azienda.com" />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome Organizzazione *</label>
                            <input type="text" name="organizationName" autoComplete="organization" value={formData.organizationName}
                                onChange={handleChange} className={ic(errors.organizationName)} placeholder="La Mia Azienda SRL" />
                            {errors.organizationName && <p className="text-red-500 text-xs mt-1">{errors.organizationName}</p>}
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password *</label>
                            <div className="relative">
                                <input type={showPassword ? 'text' : 'password'} name="password" autoComplete="new-password"
                                    value={formData.password} onChange={handleChange}
                                    className={`${ic(errors.password)} pr-10`} placeholder="••••••••" />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {formData.password && (
                                <>
                                    <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div className={`h-full transition-all ${strengthColors[score - 1] || 'bg-gray-200'}`}
                                            style={{ width: `${(score / 4) * 100}%` }} />
                                    </div>
                                    <div className="mt-1.5 grid grid-cols-2 gap-1">
                                        {PASSWORD_RULES.map(rule => {
                                            const ok = rule.test(formData.password);
                                            return (
                                                <div key={rule.id} className="flex items-center gap-1">
                                                    {ok ? <CheckCircleIcon className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                                        : <XCircleIcon className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                                                    <span className={`text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>{rule.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Conferma Password *</label>
                            <div className="relative">
                                <input type={showConfirm ? 'text' : 'password'} name="confirmPassword" autoComplete="new-password"
                                    value={formData.confirmPassword} onChange={handleChange}
                                    className={`${ic(errors.confirmPassword)} pr-10 ${formData.confirmPassword && formData.password === formData.confirmPassword ? '!border-emerald-400 !bg-emerald-50' : ''}`}
                                    placeholder="••••••••" />
                                <button type="button" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showConfirm ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {formData.confirmPassword && formData.password === formData.confirmPassword && (
                                <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1">
                                    <CheckCircleIcon className="w-3 h-3" /> Le password coincidono
                                </p>
                            )}
                            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                        </div>

                        <div>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange}
                                    className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                <span className="text-xs text-gray-600">
                                    Accetto i{' '}
                                    <Link to="/terms" className="text-blue-600 hover:underline">Termini</Link>
                                    {' '}e la{' '}
                                    <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                                </span>
                            </label>
                            {errors.acceptTerms && <p className="text-red-500 text-xs mt-1">{errors.acceptTerms}</p>}
                        </div>

                        <button type="submit" disabled={loading || !isPasswordValid(formData.password)}
                            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 text-sm">
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Creazione account...
                                </span>
                            ) : 'Crea account gratuito →'}
                        </button>
                    </form>

                    <p className="mt-5 text-center text-sm text-gray-500">
                        {t('auth.haveAccount')}{' '}
                        <Link to="/login" className="text-blue-600 hover:underline font-semibold">{t('auth.login')}</Link>
                    </p>
                </div>

                <p className="text-center text-xs text-slate-500 mt-4">
                    © {new Date().getFullYear()} InfraPilot · Tutti i diritti riservati
                </p>
            </div>
        </div>
    );
};

export default Register;